import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {EntityLineHeader} from '../../entity/entity-line-header';
import {ModelService} from '../../services/model-service';
import {GuruEntity} from '../../model/guru-entity';
import {ConfigurationService} from '../configuration.service';
import { HttpErrorResponse } from '@angular/common/http';
import {GuruAllowable} from '../../model/guru-allowable';
import {SelectionUtils} from '../selection-utils';
import {combineLatest, forkJoin, Observable} from 'rxjs';
import {MassUpdate} from '../../model/mass-update';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';

import {StringUtils} from "../../utils/string-utils";

@Component({
    selector: 'app-mass-update-dialog',
    templateUrl: './mass-update-dialog.component.html',
    styleUrls: ['./mass-update-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatCheckbox, MatButton, MatProgressSpinner]
})
export class MassUpdateDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<MassUpdateDialogComponent>>(MatDialogRef);
  private readonly modelService = inject(ModelService);
  private readonly configurationService = inject(ConfigurationService);
  private readonly data = inject(MAT_DIALOG_DATA);


  private readonly lineHeaders: EntityLineHeader[];
  private readonly leadTimeCode: string;
  filteredHeaders!: EntityLineHeader[];
  readonly commonSelections = signal<GuruEntity[]>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  selectedLines: string[] = [];
  loadedEngineKeys: Map<string, string> = new Map<string, string>();
  readonly isBusy = signal<boolean>(undefined!);
  booleanInitialValues: Map<string, boolean> = new Map<string, boolean>();

  constructor() {
    this.lineHeaders = this.data.lines;
    this.leadTimeCode = this.data.leadTimeCode;
    this.filter('');
  }

  filter(input: string): void {
    if (input && input.trim().length > 0) {
      this.filteredHeaders = this.lineHeaders.filter(header => header.description.toLowerCase().indexOf(input.toLowerCase()) >= 0);
    } else {
      this.filteredHeaders = this.lineHeaders;
    }
    this.selectedLines = [];
    this.commonSelections.set([]);
  }

  ngOnInit(): void {
    const observables: Observable<any>[] = [];
    for (const header of this.lineHeaders) {
      observables.push(this.configurationService.loadEngine(header.product, this.leadTimeCode));
    }
    this.isBusy.set(true);
    combineLatest(observables).subscribe(engines => {
      for (let i = 0; i < this.lineHeaders.length; i++) {
        const engineKey: string = engines[i].engineKey;
        const lineId: string = this.lineHeaders[i].lineId;
        this.loadedEngineKeys.set(lineId, engineKey);
        this.isBusy.set(false);
      }
    }, error => this.handleError(error));
  }

  loadCommonSelections(): void {
    this.isBusy.set(true);
    const observables: Observable<GuruEntity[]>[] = [];
    for (const header of this.filteredHeaders.filter(line => this.isSelected(line))) {
      if (this.loadedEngineKeys.has(header.lineId)) {
        const engineKey: string = this.loadedEngineKeys.get(header.lineId)!;
        observables.push(this.modelService.executeHierarchy(engineKey, 'hierMassUpdate'));
      }
    }
    forkJoin(observables).subscribe(results => {
      const allEntities: GuruEntity[] = [];
      for (let i = 0; i < results.length; i++) {
        allEntities.push(...results[i]);
      }
      this.commonSelections.set(this.removeDistinctEntities(allEntities));
      this.updateBooleanSelectionMap();
      this.isBusy.set(false);
    }, error => this.handleError(error));
  }

  private updateBooleanSelectionMap(): void {
    this.booleanInitialValues = new Map<string, boolean>();
    if (this.commonSelections() && this.commonSelections().length > 0) {
      for (const selection of this.commonSelections()) {
        if ('Boolean' === selection.EntityType) {
          this.booleanInitialValues.set(selection.Name, selection.Value);
        }
      }
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  private removeDistinctEntities(allEntities: GuruEntity[]): GuruEntity[] {
    const commonEntities: GuruEntity[] = [];
    const commonEntityNames: string[] = this.getCommonEntityNames(allEntities);
    const noChangeAllowable = new GuruAllowable();
    noChangeAllowable.Value = SelectionUtils.noChangeAllowableValue;
    noChangeAllowable.Description = ' - No Change - ';
    noChangeAllowable.IsValid = false;
    for (const name of commonEntityNames) {
      const commonEntity = allEntities.filter(entity => entity.Name === name)[0];
      if (commonEntity.EntityType === 'List') {
        const allowables: GuruAllowable[] = this.getCommonAllowables(allEntities, name);
        if (allowables && allowables.length > 0) {
          allowables.unshift(noChangeAllowable);
          commonEntity.Allowables = allowables;
          commonEntity.Value = noChangeAllowable.Value;
          commonEntities.push(commonEntity);
        }
      } else {
        if (commonEntity.EntityType === 'Numeric') {
          commonEntity.Value = '';
        }
        commonEntities.push(commonEntity);
      }
    }
    return commonEntities;
  }

  private getCommonAllowables(allEntities: GuruEntity[], entityName: string): GuruAllowable[] {
    const allowables: GuruAllowable[] = [];
    const entities: GuruEntity[] = allEntities.filter(entity => entity.Name === entityName);
    const validAllowables: GuruAllowable[] = entities[0].Allowables.filter(allowable => allowable.IsValid);
    const selectedLineCount = this.getSelectedLineCount();
    for (const valid of validAllowables) {
      const entityCount: number = entities.filter(entity => {
        return entity.Allowables.filter(allowable => allowable.IsValid && allowable.Value === valid.Value).length === 1;
      }).length;
      if (selectedLineCount === entityCount) {
        allowables.push(valid);
      }
    }
    return allowables;
  }

  private getCommonEntityNames(allEntities: GuruEntity[]): string[] {
    const commonEntityNames: string[] = [];
    const selectedLineCount: number = this.getSelectedLineCount();
    const distinctNames: string[] = [...new Set(allEntities.map(entity => entity.Name))];
    for (const name of distinctNames) {
      const linesWithEntityCount = allEntities.filter(entity => entity.Name === name).length;
      if (selectedLineCount === linesWithEntityCount) {
        commonEntityNames.push(name);
      }
    }
    return commonEntityNames;
  }

  private getSelectedLineCount(): number {
    return this.getSelectedLineHeaders().length;
  }

  private getSelectedLineHeaders(): EntityLineHeader[] {
    if (!this.filteredHeaders) {
      return [];
    }
    return this.filteredHeaders.filter(header => this.isSelected(header));
  }

  close(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    const changedEntities = this.getChangedEntities();
    const selectedLines = this.getSelectedLineHeaders();
    if (changedEntities && changedEntities.length > 0 && selectedLines && selectedLines.length > 0) {
      const update = new MassUpdate();
      update.lines = selectedLines;
      update.updatedEntities = changedEntities;
      this.dialogRef.close(update);
    }
  }

  toggleSelection(line: EntityLineHeader): void {
    this.commonSelections.set(undefined!);
    if (this.isSelected(line)) {
      this.selectedLines.splice(this.selectedLines.indexOf(line.lineId), 1);
    } else {
      this.selectedLines.push(line.lineId);
    }
  }

  isSelected(line: EntityLineHeader): boolean {
    return this.selectedLines && this.selectedLines.indexOf(line.lineId) >= 0;
  }

  setListValue(entityName: string, newValue: string): void {
    const entity = this.commonSelections().filter(selection => selection.Name === entityName)[0];
    entity.Value = newValue;
  }

  setBooleanValue(entityName: string, newValue: boolean): void {
    const entity = this.commonSelections().filter(selection => selection.Name === entityName)[0];
    entity.Value = newValue;
  }

  setNumericValue(entityName: string, newValue: string): void {
    const entity = this.commonSelections().filter(selection => selection.Name === entityName)[0];
    if (newValue && !isNaN(+newValue)) {
      entity.Value = +newValue;
    } else {
      entity.Value = '';
    }
  }

  noChangesMade(): boolean {
    return this.getChangedEntities().length === 0;
  }

  private getChangedEntities(): GuruEntity[] {
    if (!this.commonSelections() || this.commonSelections().length === 0) {
      return [];
    }
    return this.commonSelections().filter(selection => this.isSelectionChanged(selection));
  }

  private isSelectionChanged(selection: GuruEntity): boolean {
    switch (selection.EntityType) {
      case 'List':
        return selection.Value.toString() !== SelectionUtils.noChangeAllowableValue;
      case 'Boolean':
        return selection.Value !== this.getBooleanInitialValue(selection.Name);
      case 'Numeric':
        return selection.Value && selection.Value !== '';
      default:
        return false;
    }
  }

  private getBooleanInitialValue(entityName: string): boolean {
    if (!entityName || entityName.trim().length === 0) {
      return false;
    }
    if (!this.booleanInitialValues || this.booleanInitialValues.size === 0) {
      return false;
    }
    if (this.booleanInitialValues.has(entityName)) {
      return this.booleanInitialValues.get(entityName) || false;
    }
    return false;
  }

  toggleMassSelection(): void {
    if (this.allFilteredLinesSelected()) {
      this.selectedLines = [];
    } else {
      this.filteredHeaders.filter(header => !this.isSelected(header)).forEach(header => this.selectedLines.push(header.lineId));
    }
    this.commonSelections.set(undefined!);
  }

  allFilteredLinesSelected(): boolean {
    if (this.filteredHeaders && this.filteredHeaders.length > 0) {
      const selectedHeaders = this.filteredHeaders.filter(header => this.isSelected(header));
      return selectedHeaders.length === this.filteredHeaders.length;
    }
    return false;
  }

}
